import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ImageIcon, FolderOpen } from 'lucide-react';
import useImageConverter from '../hooks/useImageConverter';
import FileDropZone from '../components/ImageConverter/FileDropZone';
import ConversionOptions from '../components/ImageConverter/ConversionOptions';
import ImagePreview from '../components/ImageConverter/ImagePreview';
import ConversionResults from '../components/ImageConverter/ConversionResults';
import ProgressBar from '../components/ImageConverter/ProgressBar';

export default function ImageConverterPage({ user }) {
  const converter = useImageConverter();

  if (!user) {
    return (
      <div className="flex-grow flex flex-col items-center justify-center text-center p-8 animate-fade-in">
        <div className="bg-slate-800/50 backdrop-blur-md p-8 md:p-12 rounded-3xl shadow-2xl w-full max-w-md border border-slate-700/50">
          <h2 className="text-3xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-cyan-400">
            Authentication Required
          </h2>
          <p className="text-gray-300 mb-8 text-lg">Please sign in to access this feature.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Free Online Image Converter — PNG, HEIC, WebP to JPEG/PNG | JustAutomateIt</title>
        <meta name="description" content="Convert images free online. Supports PNG, HEIC, WebP, AVIF to JPEG or PNG with filters, scaling, and background removal. Runs in your browser — 100% private." />
        <meta property="og:title" content="Free Online Image Converter | JustAutomateIt" />
        <meta property="og:description" content="Convert PNG, HEIC, WebP, AVIF to JPEG/PNG with filters and batch processing. Free, private, no upload needed." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="flex-grow flex flex-col items-center px-4 py-8 bg-gradient-to-br from-gray-900 via-slate-900 to-black min-h-screen">
        {/* Hero */}
        <div className="text-center mb-8 animate-fade-in">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-cyan-400">
            Free Online Image Converter
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Convert PNG, HEIC, WebP, and AVIF to JPEG or PNG instantly in your browser.
            Apply filters, remove backgrounds, scale, and batch-convert — no upload needed, 100% private.
          </p>
        </div>

        <div className="w-full max-w-4xl space-y-6">
          {/* Mode Toggle */}
          <div className="flex justify-center gap-2 animate-fade-in">
            <button
              onClick={() => { converter.setCurrentMode('individual'); converter.clearConverterOutputs(); }}
              className={`flex items-center gap-2 font-semibold py-2.5 px-5 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 ${
                converter.currentMode === 'individual'
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/20'
                  : 'bg-slate-800/50 text-gray-400 hover:text-white hover:bg-slate-700/50 border border-slate-700/50'
              }`}
            >
              <ImageIcon size={16} />
              Individual File
            </button>
            <button
              onClick={() => { converter.setCurrentMode('folder'); converter.clearConverterOutputs(); }}
              className={`flex items-center gap-2 font-semibold py-2.5 px-5 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 ${
                converter.currentMode === 'folder'
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/20'
                  : 'bg-slate-800/50 text-gray-400 hover:text-white hover:bg-slate-700/50 border border-slate-700/50'
              }`}
            >
              <FolderOpen size={16} />
              Folder (Bulk)
            </button>
          </div>

          {/* File Drop Zone */}
          <div className="bg-slate-800/50 backdrop-blur-md rounded-2xl border border-slate-700/50 p-6 animate-slide-up">
            <FileDropZone
              mode={converter.currentMode}
              inputText={converter.currentMode === 'individual' ? converter.fileInputIndividualText : converter.folderInputText}
              inputRef={converter.currentMode === 'individual' ? converter.fileInputIndividualRef : converter.folderInputRef}
              onFileChange={converter.currentMode === 'individual' ? converter.handleFileIndividualChange : converter.handleFolderInputChange}
              onDrop={converter.handleDrop}
              onDragOver={converter.handleDragOver}
              disabled={converter.isConverting}
            />

            <button
              onClick={converter.handleConversion}
              disabled={converter.filesToConvert.length === 0 || converter.isConverting}
              className="w-full mt-4 flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 disabled:from-slate-600 disabled:to-slate-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-xl shadow-lg shadow-indigo-500/20 transition-all duration-300 transform hover:scale-[1.02] disabled:hover:scale-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
            >
              {converter.isConverting ? 'Converting...' : `Convert to ${converter.outputFormat.toUpperCase()}`}
            </button>
          </div>

          {/* Preview */}
          <ImagePreview
            canvasRef={converter.previewCanvasRef}
            visible={converter.previewVisible}
          />

          {/* Options */}
          <ConversionOptions
            outputFormat={converter.outputFormat} setOutputFormat={converter.setOutputFormat}
            jpegQualityValue={converter.jpegQualityValue} setJpegQualityValue={converter.setJpegQualityValue}
            filtersEnabled={converter.filtersEnabled} setFiltersEnabled={converter.setFiltersEnabled}
            removeBackground={converter.removeBackground} setRemoveBackground={converter.setRemoveBackground}
            backgroundTolerance={converter.backgroundTolerance} setBackgroundTolerance={converter.setBackgroundTolerance}
            cropEnabled={converter.cropEnabled} setCropEnabled={converter.setCropEnabled}
            cropSides={converter.cropSides} updateCropSide={converter.updateCropSide}
            autoScale={converter.autoScale} setAutoScale={converter.setAutoScale}
            scaleFactor={converter.scaleFactor} setScaleFactor={converter.setScaleFactor}
            filters={converter.filters} updateFilter={converter.updateFilter} resetFilters={converter.resetFilters}
          />

          {/* Progress */}
          {converter.isConverting && (
            <ProgressBar
              current={converter.conversionProgress.current}
              total={converter.conversionProgress.total}
              fileName={converter.conversionProgress.fileName}
            />
          )}

          {/* Message */}
          {converter.message.visible && (
            <div
              role="status"
              aria-live="polite"
              className={`p-4 rounded-xl text-sm text-center font-medium border animate-fade-in ${
                converter.message.type === 'success' ? 'bg-green-500/10 border-green-500/30 text-green-300' :
                converter.message.type === 'error' ? 'bg-red-500/10 border-red-500/30 text-red-300' :
                'bg-indigo-500/10 border-indigo-500/30 text-indigo-300'
              }`}
            >
              {converter.message.text}
            </div>
          )}

          {/* Results */}
          <ConversionResults
            results={converter.results}
            showDownloadAll={converter.showDownloadAll}
            showClearResults={converter.showClearResults}
            onDownloadAll={converter.handleDownloadAll}
            onClear={() => { converter.clearConverterOutputs(); converter.setMessage(m => ({ ...m, visible: false })); }}
          />

          {/* Internal Links */}
          <div className="text-center text-sm text-gray-500 pt-4 border-t border-slate-800">
            Also try our{' '}
            <span className="text-indigo-400 cursor-pointer hover:text-indigo-300 transition-colors">Batch Image Editor</span>
            {' '}and{' '}
            <span className="text-indigo-400 cursor-pointer hover:text-indigo-300 transition-colors">Product Image Generator</span>
          </div>
        </div>
      </div>
    </>
  );
}
